import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'

import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import { useEmployeesSalaryRangesConformance } from 'modules/job_catalog/hooks/useEmployeesSalaryRangesConformance'
import { Role } from 'modules/job_catalog/hooks/useRole'
import {
  type SalaryDistribution,
  useSalariesDistribution,
} from 'modules/job_catalog/hooks/useSalariesDistribution'
import { employeeJobLevel } from 'modules/job_catalog/store/collections/levels'
import { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import Blank from './Blank'
import Card from './Card'
import Legend from './Legend'
import SalaryGenderBar from './SalaryGenderBar'
import SalaryScatterPlot from './SalaryScatterPlot'

interface Props {
  role: Role
  contractVersions: ContractVersions
}

const useSalaryData = createComputed((salaries: SalaryDistribution[]) => {
  if (!salaries) return { hasRanges: 0, hasSalary: 0 }

  return salaries.reduce(
    (acc, item) => {
      const hasSalary = item.contractCuratedSalary ? (acc.hasSalary += 1) : acc.hasSalary

      const hasRanges =
        item.curatedJobCatalogLevelSalaryMaxRange || item.curatedJobCatalogLevelSalaryMinRange
          ? (acc.hasRanges += 1)
          : acc.hasRanges

      return { hasSalary: hasSalary, hasRanges: hasRanges }
    },
    { hasRanges: 0, hasSalary: 0 }
  )
})

const SalaryInsights: React.FC<Props> = ({ role, contractVersions }) => {
  const roleEmployees = useMemo(
    () =>
      employees.active.filter(
        (x) => employeeJobRole(contractVersions, x)?.attributes.id === role.id
      ),
    [role.id]
  )
  const [employeesSalaryRangesConformance, isFetchingEmployeesSalaryRangesConformance] =
    useEmployeesSalaryRangesConformance(roleEmployees.map((x) => x.id))
  const [salariesDistributions, isFetchingSalariesDistributions] = useSalariesDistribution(role.id)
  const salaryData = useSalaryData(salariesDistributions).get()

  const getEmployees = useUnsafeMemo(
    () => (levelId: number) =>
      roleEmployees.filter((x) => employeeJobLevel(contractVersions, x)?.attributes.id === levelId),
    [roleEmployees]
  )

  useAsyncData(async () => {
    await policyAuthorizations.authorizePolicies('contracts.assign_jobs', employees.active)
  }, [])

  const hasAssignJobsPermissions = employees.active.some((x) =>
    policyAuthorizations.for('contracts.assign_jobs', x)
  )

  const employeesPerLevel =
    role.levels &&
    role.levels
      .map((x) => ({
        level: x.name,
        employees: getEmployees(x.id).length,
        order: x.order,
      }))
      .sort((a, b) => a.order - b.order)

  const totalEmployees = employeesPerLevel.reduce((acc, item) => {
    return acc + item.employees
  }, 0)

  const hasGrossSalaries = salaryData.hasSalary > 0
  const areEmployeesAssigned = totalEmployees > 0

  return (
    <>
      {(isFetchingSalariesDistributions || isFetchingEmployeesSalaryRangesConformance) && (
        <Box justifyContent="center" alignItems="center" paddingBottom="s40">
          <Spinner />
        </Box>
      )}
      {!isFetchingSalariesDistributions && !isFetchingEmployeesSalaryRangesConformance && (
        <Box flexDirection="row" gap="s24" flexWrap="wrap" alignItems="flexStart">
          <Card>
            <Card.Header title={i18n.t('job_catalog.level_distribution')}>
              {areEmployeesAssigned && (
                <Card.Header.Subtitle
                  value={totalEmployees}
                  label={i18n.t('job_catalog.salary_insights.employees_with_role')}
                />
              )}
            </Card.Header>
            <Box>
              {areEmployeesAssigned ? (
                <>
                  <SalaryGenderBar data={employeesPerLevel} />
                  <Legend>
                    <Legend.Item color="purple1000">
                      {i18n.t('job_catalog.number_of_employees')}
                    </Legend.Item>
                  </Legend>
                </>
              ) : (
                <Blank
                  icon="group"
                  title={i18n.t('job_catalog.salary_insights.no_employees_assigned')}
                  label={i18n.t('job_catalog.salary_insights.no_employees_assigned_message')}
                />
              )}
            </Box>
          </Card>
          {hasAssignJobsPermissions && (
            <Card>
              <Card.Header
                title={i18n.t('job_catalog.salary_map')}
                tooltip={i18n.t('job_catalog.salary_insights.permissions_tooltip')}
              >
                {areEmployeesAssigned && hasGrossSalaries && (
                  <>
                    <Card.Header.Subtitle
                      value={employeesSalaryRangesConformance?.inRangeCount || 0}
                      label={i18n.t('job_catalog.salary_insights.employees_on_range')}
                    />
                    <Card.Header.Subtitle
                      value={employeesSalaryRangesConformance?.outOfRangeCount || 0}
                      label={i18n.t('job_catalog.salary_insights.employees_out_of_range')}
                    />
                  </>
                )}
              </Card.Header>
              <Box>
                {areEmployeesAssigned && hasGrossSalaries && (
                  <>
                    <SalaryScatterPlot roleId={role.id} />
                    <Legend>
                      <Legend.Item color="viridian800">
                        {i18n.t('job_catalog.salary_insights.target_salary_range')}
                      </Legend.Item>
                      <Legend.Item color="purple1000">
                        {i18n.t('job_catalog.salary_insights.employees_current_salary')}
                      </Legend.Item>
                    </Legend>
                  </>
                )}
                {!areEmployeesAssigned && (
                  <Blank
                    icon="group"
                    title={i18n.t('job_catalog.salary_insights.no_employees_assigned')}
                    label={i18n.t('job_catalog.salary_insights.no_employees_salaries')}
                  />
                )}
                {areEmployeesAssigned && !hasGrossSalaries && (
                  <Blank
                    icon="wallet"
                    title={i18n.t('job_catalog.salary_insights.no_salary')}
                    label={i18n.t('job_catalog.salary_insights.no_gross_salary')}
                  />
                )}
              </Box>
            </Card>
          )}
        </Box>
      )}
    </>
  )
}
export default SalaryInsights
